import React, { useMemo, useState } from "react";

/* ───────────────────────────────────────────────────────────────────────────
 * LcsPortfolioTable — modal-style ranked-portfolio view of LCS staged leases.
 *
 * Pure presentation. Reads the lcsStagedLeases.json bundle, lets the operator
 * filter / search / sort / select, and emits onSelect(leaseId) when a row is
 * clicked.
 *
 * Props:
 *   open             — boolean
 *   onClose          — fn
 *   leases           — array (each entry from src/data/lcsStagedLeases.json)
 *   selectedLeaseId  — currently-selected lease in the cockpit
 *   onSelect         — fn(leaseId) → void
 * ──────────────────────────────────────────────────────────────────────── */

const C = {
  bg:        "rgba(6,10,19,0.92)",
  panel:     "#0f1629",
  card:      "#131b30",
  cardHover: "#1a2442",
  border:    "rgba(56,78,122,0.3)",
  borderLit: "rgba(80,120,180,0.45)",
  text:      "#e2e8f0",
  textSec:   "#94a3b8",
  textDim:   "#64748b",
  textFaint: "#475569",
  cyan:      "#22d3ee", cyanDim: "rgba(34,211,238,0.15)",
  green:     "#34d399", greenDim:"rgba(52,211,153,0.12)",
  red:       "#f87171", redDim:  "rgba(248,113,113,0.12)",
  yellow:    "#fbbf24", yellowDim:"rgba(251,191,36,0.12)",
  blue:      "#3b82f6",
  mono:      "'JetBrains Mono','SF Mono','Fira Code',Consolas,monospace",
  sans:      "'Inter','Segoe UI',Roboto,sans-serif",
};

const recColor = (rec) => {
  if (rec === "Optimize") return C.green;
  if (rec === "Monitor") return C.yellow;
  if (rec === "Investigate Decline") return C.yellow;
  if (rec === "No Action") return C.textSec;
  if (rec === "Insufficient Data") return C.textFaint;
  if (rec === "Exclude from producer ranking") return C.textFaint;
  return C.textSec;
};
const confColor = (c) => c === "high" ? C.green : c === "medium" ? C.yellow : C.textDim;
const fmt = (n, d = 1) => Number.isFinite(n) ? n.toFixed(d) : "—";

function tierBadge(tier) {
  const map = {
    tier1: { bg: C.greenDim, fg: C.green, t: "★★★" },
    tier2: { bg: C.cyanDim, fg: C.cyan, t: "★★" },
    tier3: { bg: C.yellowDim, fg: C.yellow, t: "★" },
    operational: { bg: "rgba(120,120,140,0.15)", fg: C.textDim, t: "OPS" },
    skip: { bg: C.redDim, fg: C.red, t: "—" },
  };
  return map[tier] || { bg: "transparent", fg: C.textDim, t: "?" };
}

function flagBadges(r) {
  const out = [];
  if (r.modelAgeDays != null && r.modelAgeDays > 30) {
    out.push({ k: "stale", t: `${r.modelAgeDays}d stale`, fg: C.yellow, bg: C.yellowDim });
  }
  if ((r.forecastRows || 0) === 0) {
    out.push({ k: "no-fcst", t: "no forecast", fg: C.red, bg: C.redDim });
  }
  if (r.assetTypeHint && r.assetTypeHint !== "producer") {
    out.push({ k: "ops", t: r.assetTypeHint, fg: C.textDim, bg: "rgba(120,120,140,0.15)" });
  }
  if (r.status === "no_data") {
    out.push({ k: "no-data", t: "no data", fg: C.red, bg: C.redDim });
  }
  return out;
}

const COLUMNS = [
  { key: "rank",   label: "#",    w: 36,  align: "right" },
  { key: "leaseId", label: "Lease", w: 110, align: "left" },
  { key: "score",   label: "Score", w: 60,  align: "right" },
  { key: "rec",     label: "Recommendation", w: 170, align: "left" },
  { key: "conf",    label: "Conf", w: 60, align: "left" },
  { key: "oil",     label: "Oil bbl/d", w: 80, align: "right" },
  { key: "gas",     label: "Gas mcf/d", w: 80, align: "right" },
  { key: "water",   label: "Water bbl/d", w: 90, align: "right" },
  { key: "head",    label: "Headline / risk", w: 360, align: "left" },
  { key: "flags",   label: "Flags", w: 160, align: "left" },
  { key: "act",     label: "", w: 60, align: "center" },
];

export default function LcsPortfolioTable({ open, onClose, leases, selectedLeaseId, onSelect }) {
  const [filterText, setFilterText] = useState("");
  const [showOperational, setShowOperational] = useState(false);
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState("desc");

  const rows = useMemo(() => {
    const q = filterText.trim().toLowerCase();
    let r = (leases || []).map((x, i) => ({ ...x, _origRank: i + 1 }));
    if (!showOperational) {
      r = r.filter((x) => x.assetTypeHint === "producer");
    }
    if (q) {
      r = r.filter((x) =>
        (x.leaseId || "").toLowerCase().includes(q) ||
        (x.leaseName || "").toLowerCase().includes(q),
      );
    }
    const dir = sortDir === "asc" ? 1 : -1;
    r.sort((a, b) => {
      switch (sortKey) {
        case "rank":  return (a._origRank - b._origRank) * dir;
        case "leaseId": return (a.leaseId || "").localeCompare(b.leaseId || "") * dir;
        case "score": return ((a.score ?? -1) - (b.score ?? -1)) * dir;
        case "rec":   return (a.recommendation || "").localeCompare(b.recommendation || "") * dir;
        case "conf":  {
          const order = { high: 2, medium: 1, low: 0 };
          return ((order[a.confidence] ?? -1) - (order[b.confidence] ?? -1)) * dir;
        }
        case "oil":   return ((a.recentOilAvg ?? -1) - (b.recentOilAvg ?? -1)) * dir;
        case "gas":   return ((a.recentGasAvg ?? -1) - (b.recentGasAvg ?? -1)) * dir;
        case "water": return ((a.recentWaterAvg ?? -1) - (b.recentWaterAvg ?? -1)) * dir;
        default:      return 0;
      }
    });
    return r;
  }, [leases, filterText, showOperational, sortKey, sortDir]);

  const totals = useMemo(() => {
    const all = leases || [];
    return {
      total: all.length,
      producers: all.filter((x) => x.assetTypeHint === "producer").length,
      tier1: all.filter((x) => x.demoTier === "tier1").length,
      withForecast: all.filter((x) => (x.forecastRows || 0) > 0).length,
      stale: all.filter((x) => (x.modelAgeDays ?? 0) > 30).length,
    };
  }, [leases]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "leaseId" ? "asc" : "desc"); }
  }

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: C.bg, zIndex: 1000,
        display: "flex", alignItems: "stretch", justifyContent: "center",
        padding: "32px 40px", fontFamily: C.sans, color: C.text,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.panel, border: `1px solid ${C.border}`,
          width: "100%", maxWidth: 1320, display: "flex", flexDirection: "column",
          borderRadius: 4, boxShadow: "0 32px 80px rgba(0,0,0,0.7)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", padding: "10px 14px",
          borderBottom: `1px solid ${C.border}`, gap: 12,
        }}>
          <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: "0.08em",
                          fontFamily: C.mono, color: C.textSec }}>
            RANKED LCS LEASE OPPORTUNITIES
          </span>
          <span style={{ fontSize: 10, color: C.textDim, fontFamily: C.mono }}>
            {totals.tier1} tier-1 of {totals.producers} producers · {totals.withForecast} with forecast
            {totals.stale ? ` · ${totals.stale} forecast(s) > 30d stale` : ""}
          </span>
          <div style={{ flex: 1 }} />
          <input
            type="text"
            placeholder="Search leaseId or name…"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{
              padding: "5px 10px", fontSize: 12, fontFamily: C.mono,
              border: `1px solid ${C.border}`, background: C.card, color: C.text,
              borderRadius: 2, width: 240,
            }}
          />
          <label style={{ fontSize: 11, color: C.textSec, display: "flex", gap: 4, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={showOperational}
              onChange={(e) => setShowOperational(e.target.checked)}
            />
            include INJ/SWD/meters
          </label>
          <button
            onClick={onClose}
            style={{
              fontSize: 11, padding: "4px 10px", fontFamily: C.mono,
              background: "transparent", color: C.textSec,
              border: `1px solid ${C.border}`, borderRadius: 2, cursor: "pointer",
            }}
          >
            ✕ Close
          </button>
        </div>

        {/* Table */}
        <div style={{ overflow: "auto", flex: 1, padding: "0 0 12px 0" }}>
          <table style={{
            width: "100%", borderCollapse: "collapse", fontSize: 11,
            fontFamily: C.sans, fontVariantNumeric: "tabular-nums",
          }}>
            <thead style={{ position: "sticky", top: 0, background: C.panel, zIndex: 1 }}>
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => c.key !== "head" && c.key !== "flags" && c.key !== "act" && toggleSort(c.key)}
                    style={{
                      textAlign: c.align,
                      padding: "8px 10px",
                      fontSize: 10,
                      fontFamily: C.mono,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: sortKey === c.key ? C.cyan : C.textDim,
                      borderBottom: `1px solid ${C.border}`,
                      cursor: (c.key === "head" || c.key === "flags" || c.key === "act") ? "default" : "pointer",
                      userSelect: "none",
                      width: c.w,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {c.label}
                    {sortKey === c.key && (sortDir === "asc" ? " ↑" : " ↓")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} style={{
                    textAlign: "center", padding: 36, color: C.textDim, fontSize: 11,
                  }}>
                    No leases match.
                  </td>
                </tr>
              )}
              {rows.map((r, idx) => {
                const isSel = r.leaseId === selectedLeaseId;
                const tier = tierBadge(r.demoTier);
                const flags = flagBadges(r);
                return (
                  <tr
                    key={r.leaseId}
                    onClick={() => onSelect?.(r.leaseId)}
                    style={{
                      cursor: "pointer",
                      background: isSel ? C.cardHover : (idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)"),
                      borderLeft: isSel ? `3px solid ${C.cyan}` : "3px solid transparent",
                    }}
                    onMouseEnter={(e) => { if (!isSel) e.currentTarget.style.background = C.card; }}
                    onMouseLeave={(e) => { if (!isSel) e.currentTarget.style.background = idx % 2 === 0 ? "transparent" : "rgba(255,255,255,0.015)"; }}
                  >
                    <td style={{ padding: "6px 10px", textAlign: "right", color: C.textDim, fontFamily: C.mono }}>{idx + 1}</td>
                    <td style={{ padding: "6px 10px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={{
                          fontSize: 9, fontWeight: 800, padding: "1px 5px", borderRadius: 2,
                          background: tier.bg, color: tier.fg, fontFamily: C.mono,
                        }}>{tier.t}</span>
                        <span style={{ fontFamily: C.mono, color: C.text, fontWeight: 700 }}>{r.leaseId}</span>
                      </div>
                      <div style={{ fontSize: 10, color: C.textDim, marginTop: 1 }}>{r.leaseName}</div>
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right",
                                  fontFamily: C.mono, fontWeight: 800, color: C.text }}>
                      {Number.isFinite(r.score) ? r.score.toFixed(1) : "—"}
                    </td>
                    <td style={{ padding: "6px 10px" }}>
                      <span style={{ color: recColor(r.recommendation), fontWeight: 700 }}>
                        {r.recommendation || "—"}
                      </span>
                    </td>
                    <td style={{ padding: "6px 10px", color: confColor(r.confidence),
                                 fontFamily: C.mono, textTransform: "uppercase", fontSize: 10 }}>
                      {r.confidence || "—"}
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: C.mono, color: C.text }}>
                      {fmt(r.recentOilAvg, 2)}
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: C.mono, color: C.text }}>
                      {fmt(r.recentGasAvg, 2)}
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "right", fontFamily: C.mono, color: C.text }}>
                      {fmt(r.recentWaterAvg, 1)}
                    </td>
                    <td style={{ padding: "6px 10px", color: C.textSec, fontSize: 11, lineHeight: 1.35 }}>
                      {r.inference?.headline || r.summary || "—"}
                    </td>
                    <td style={{ padding: "6px 10px" }}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                        {flags.length === 0 && <span style={{ color: C.textFaint, fontSize: 10 }}>—</span>}
                        {flags.map((f) => (
                          <span key={f.k} style={{
                            fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 2,
                            background: f.bg, color: f.fg, fontFamily: C.mono,
                          }}>{f.t}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: "6px 10px", textAlign: "center" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, color: C.cyan,
                        textDecoration: isSel ? "none" : "underline",
                      }}>
                        {isSel ? "Selected" : "View"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer / caveats */}
        <div style={{
          padding: "8px 14px", borderTop: `1px solid ${C.border}`,
          fontSize: 10, color: C.textDim, fontFamily: C.mono,
          display: "flex", gap: 16, flexWrap: "wrap",
        }}>
          <span>tier1 producers + forecast = demo-ready</span>
          <span>·</span>
          <span>OPS = injector / meter / SWD / water-transfer</span>
          <span>·</span>
          <span>"stale" = forecast generated &gt; 30 days ago</span>
          <span>·</span>
          <span>economics in detail panel use editable default cost deck — not per-lease actuals</span>
        </div>
      </div>
    </div>
  );
}
